import {LitElement, html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {connect} from 'pwa-helpers';
import {classMap} from 'lit/directives/class-map.js';
import {when} from 'lit/directives/when.js';
import {map} from 'lit/directives/map.js';
import {range} from 'lit/directives/range.js';
import {msg} from '@lit/localize';

import store from '../../../store';
import {
	selectGroupedHeads, selectOlivioBase,
	selectOlivioBaseDescription,
	selectOlivioBaseNumber,
	selectOlivioBracketDescription,
	selectOlivioBracketNumber,
	selectOlivioBracketPrice, selectOlivioBuriedBaseSize, selectOlivioCandelabraTop, selectOlivioCandelabraTopAngles, selectOlivioCandelabraTopLabel,
	selectOlivioDesignIsCandelabra,
	selectOlivioDesignIsFloracion,
	selectOlivioDesignIsSistema, selectOlivioDiameter, selectOlivioFuseBox, selectOlivioFuseBoxDescription, selectOlivioFuseBoxNumber,
	selectOlivioHeads,
	selectOlivioNumberOfHeads,
	selectOlivioPoleDescription,
	selectOlivioPoleDiameter,
	selectOlivioPoleHeight, selectOlivioPoleHeightValue, selectOlivioPoleMaterial,
	selectOlivioPoleNumber, selectOlivioSecondDoor, selectOlivioSecondDoorDescription, selectOlivioSecondDoorLabel,
	selectOlivioSeparateBuriedBase,
	selectOlivioSeparateBuriedBaseDescription,
	selectOlivioSeparateBuriedBaseHeight,
	selectOlivioSeparateBuriedBaseNumber, selectOlivioSeparateBuriedBasePrice, selectOlivioWoodPoleNumber,
} from '../../olivioSelectors';
import {
	selectLanguage,
	selectProductData,
	selectProductDatas,
	selectSpcConfiguration,
	selectSpcConfigurations,
	selectSpcSessions
} from '../../../selectors';
import {
	olivioHeadGoboApertureOptions,
	olivioHeadTypeOptions
} from '../../olivioModel';
import {getValueOfFeature} from '../../../util/values';
import {getHeadPrice} from '../../olivioHeadPrices';
import {filterAndJoin} from '../../../util/filterAndJoin';
import {conditional} from '../../../util/conditional';

@customElement('olivio-specsheet-head-table')
export class OlivioSpecsheetHeadTable extends connect(store)(LitElement) {

	static styles = [
		css`
			:host {
				width: 100%;
				container-type: inline-size;
			}
			.headline {
				font: var(--font-specsheet-print-small-bold);
				font-weight: bold;
				font-size: 1.4cqw;
			}
			.index {
				display: flex;
				justify-content: center;
				align-items: center;
				background-color: black;
				font-size: 1cqw;
				color: white;
				height: 1.5cqw;
				width: 1.5cqw;
				border-radius: 1cqw;
			}
			table {
				border-collapse: collapse;
				width: 100%;
				container-type: inline-size;
			}
			th {
				vertical-align: top;
				font: var(--font-specsheet-print-small-bold);
				font-weight: bold;
				font-size: 1cqw;
				text-align: left;
				padding: 0.7cqw 0.5cqw 0.7cqw 0;
				border-bottom: 1px solid #c6c6c6;
			}
			td {
				vertical-align: top;
				font: var(--font-specsheet-print-small-text);
				font-weight: 300;
				font-size: 1cqw;
				text-align: left;
				padding: 0.7cqw 0.5cqw 0.7cqw 0;
				border-bottom: 1px solid #c6c6c6;
			}
			.nobreak {
				white-space: nowrap;
			}
			.price {
				text-align: right;
				padding-left: 1cqw;
			}
			.grow {
				width: 100%;
			}
		`];

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}

	connectedCallback() {
		super.connectedCallback();
	}
	stateChanged(state) {
		this.state = state;
		this.loggedIn = state.userProfile.data.loggedIn;
		this.lang = selectLanguage(state);
		this.numberOfHeads = selectOlivioNumberOfHeads(state);
		this.heads = selectOlivioHeads(state).filter(h => h.active);
		this.groupedHeads = selectGroupedHeads(state);
	}
	getSpcInstanceIdForHead(index) {
		return this.groupedHeads.find(headGroup => headGroup.indexes.includes(index)).instanceId;
	}
	getHeadRow(instance) {
		const isCandelabra = selectOlivioDesignIsCandelabra(this.state);
		let height;
		let angle;
		if (isCandelabra) {
			height = Math.floor(selectOlivioPoleHeightValue(this.state) * 1000);
			// directly get the head orientation from model
			const top = selectOlivioCandelabraTop(this.state); // 1.000, 2.120, 2.180, 3.000
			const angles = selectOlivioCandelabraTopAngles(this.state); // 1.000, 2.120, 2.180, 3.000
			angle = angles[instance];
		} else {
			height = Math.floor(this.heads[instance].height  * 1000);
			angle = this.heads[instance].angle;
		}
		const type = this.heads[instance].type;
		const housingSize = this.heads[instance].housingSize;
		const colourTemperature = this.heads[instance].colourTemperature;
		const lightDistribution = this.heads[instance].lightDistribution;
		const goboAperture = this.heads[instance].goboAperture;

		const clt = getValueOfFeature('root::EB_ConstantLumenTechnology_1', selectSpcConfiguration(this.state));
		const zhaga = getValueOfFeature('root::EB_Zhaga_1', selectSpcConfiguration(this.state));
		const protection = getValueOfFeature('root::EB_ElectricalProtectionClass_1', selectSpcConfiguration(this.state));
		const control = getValueOfFeature('root::EB_Control_1', selectSpcConfiguration(this.state));

		const olivioHeadTypeOption = olivioHeadTypeOptions.find((option) => option.id === type);
		const typeLabel = olivioHeadTypeOption.label[this.lang];
		const typeInfo = olivioHeadTypeOption.info[this.lang];
		const typeDescription = olivioHeadTypeOption.description[this.lang];

		let goboApertureDescription;
		let goboApertureNumber;
		let goboAperturePrice;
		if (type === 'gobo') {
			const goboApertureOption = olivioHeadGoboApertureOptions.find((option) => option.id === goboAperture);
			goboApertureDescription = goboApertureOption.description[this.lang];
			goboApertureNumber = goboApertureOption.number;
			goboAperturePrice = goboApertureOption.price;
		}
		const accessory = getValueOfFeature('LE_Accessories_1', selectSpcConfiguration(this.state));

		// get the sx number from CCV
		let spcConfiguration = null;
		let sx = '';

		const instanceId = this.getSpcInstanceIdForHead(instance);
		const spcConfigurations = selectSpcConfigurations(this.state);
		if (Object.hasOwn(spcConfigurations, instanceId)) {
			spcConfiguration = spcConfigurations[instanceId];
		}
		const conditionalVariantValues = this.state.productData.conditionalVariantValues;
		if (spcConfigurations && conditionalVariantValues) {
			sx = conditional(spcConfiguration, conditionalVariantValues, 'LU_LegacyArticleNumberGerman');
		}

		return html`
			<tr class="row">
				<td><span class="index">${instance + 1}</span></td>
				<td class="nobreak">${height} mm</td>
				<td class="nobreak">${angle}°</td>
				<td class="grow">
					${when(type === 'luminaire', () => html`
						<trans-label instanceId="${this.getSpcInstanceIdForHead(instance)}" key="HS_HousingSize" value="${housingSize}" context="FV"></trans-label><br>
						<trans-label instanceId="${this.getSpcInstanceIdForHead(instance)}" key="LE_LightDistribution_1" value="${lightDistribution}" context="FV"></trans-label>,
						<trans-label instanceId="${this.getSpcInstanceIdForHead(instance)}" key="LE_ColourTemperature_1" value="${colourTemperature}" context="FV"></trans-label>,
						<trans-label key="LE_DimmingLevel_1" context="FV"></trans-label> lm,
						<trans-label key="LE_ReflectorColour_1" context="FV.S"></trans-label><br>

						<trans-label key="EB_Control_1" context="FV.S"></trans-label>,
						${when(clt !== 'None', () => html`<trans-label key="EB_ConstantLumenTechnology_1" context="F"></trans-label>,`)}
						${when(zhaga !== 'None', () => html`<trans-label key="EB_Zhaga_1" context="F"></trans-label> <trans-label key="EB_Zhaga_1" context="FV.S"></trans-label>,`)}
						<trans-label key="EB_ElectricalProtectionClass_1" context="FV.S"></trans-label><br>

						${when(accessory !== 'None', () => html`
							<spc-values-label class="value" attributeKey="LE_Accessories_1" context="FV.S" .showIdWhenMissing=${true}></spc-values-label>
						`)}
					`)}
					${when(type === 'gobo', () => html`
						<trans-label instanceId="${this.getSpcInstanceIdForHead(instance)}" key="HS_HousingSize" value="${housingSize}" context="FV"></trans-label><br>
						${typeLabel}, ${typeInfo}<br>
						${goboApertureDescription}<br>
					`)}
					${when(type === 'camera', () => html`
						<trans-label instanceId="${this.getSpcInstanceIdForHead(instance)}" key="HS_HousingSize" value="${housingSize}" context="FV"></trans-label><br>
						${typeInfo}
					`)}
					${when(type === 'speaker', () => html`
						<trans-label instanceId="${this.getSpcInstanceIdForHead(instance)}" key="HS_HousingSize" value="${housingSize}" context="FV"></trans-label><br>
						${typeInfo}
					`)}
				</td>
				<td class="nobreak">
					${when(type === 'luminaire', () => html`
						${sx}
					`)}
					${when(type === 'gobo' || type === 'camera', () => html`
						${olivioHeadTypeOption.number}
					`)}
					${when(type === 'gobo', () => html`
						<br>${goboApertureNumber}
					`)}
					${when(type === 'speaker', () => html`
						${olivioHeadTypeOption.number[housingSize]}
					`)}
				</td>
				${when(this.loggedIn, () => html`
					<td class="nobreak price">
						${when(type === 'luminaire', () => html`
							${getHeadPrice(sx, clt, zhaga, protection, control, accessory, housingSize)} €
						`)}
						${when(type === 'gobo' || type === 'camera', () => html`
							${olivioHeadTypeOption.price} €
						`)}
						${when(type === 'gobo', () => html`
							<br>${goboAperturePrice} €
						`)}
						${when(type === 'speaker', () => html`
							${olivioHeadTypeOption.price[housingSize]} €
						`)}
					</td>
				`)}
			</tr>
		`;
	}
	render() {
		const s = store.getState();
		if (!selectOlivioDesignIsSistema(s) && !selectOlivioDesignIsFloracion(s) && !selectOlivioDesignIsCandelabra(s)) return null;

		return html`
			<table class="table heads">
				<tr class="headerRow">
					<th>${msg('No.')}</th>
					<th>${msg('Height')}</th>
					<th>${msg('Angle')}</th>
					<th>${msg('Head')}</th>
					<th>${msg('SKU')}</th>
					${when(this.loggedIn, () => html`
						<th class="nobreak price">${msg('Price')}</th>
					`)}
				</tr>
				${map(range(this.numberOfHeads), (i) => this.getHeadRow(i))}
			</table>
			<br><br>
			<table class="table accessories">
				${when(selectOlivioDesignIsSistema(s), () => html`
					<tr>
						<th>${msg('Bracket')}</th>
						<td class="grow">${this.numberOfHeads} ${selectOlivioBracketDescription(s)}</td>
						<td class="nobreak">${selectOlivioBracketNumber(s)}</td>
						${when(this.loggedIn, () => html`
							<td class="nobreak price">${selectOlivioBracketPrice(s)} €</td>
						`)}
					</tr>
				`)}
				${when(selectOlivioDesignIsCandelabra(s), () => html`
					<tr>
						<th>${msg('Bracket')}</th>
						<td class="grow">${selectOlivioCandelabraTopLabel(s)}</td>
						<td class="nobreak"></td>
						${when(this.loggedIn, () => html`
							<td class="nobreak price"></td>
						`)}
						</tr>
				`)}
				<tr>
					<th>${msg('Pole')}</th>
					<td class="grow">${selectOlivioPoleDescription(s)}, ${msg('Height')} ${selectOlivioPoleHeight(s)} m, ${msg('Diameter')} (${msg('top')}) ${selectOlivioDiameter(s)} mm</td>
					<td class="nobreak">
						${selectOlivioPoleMaterial(s) === 'steel' ? selectOlivioPoleNumber(s) : selectOlivioWoodPoleNumber(s)}
					</td>
					${when(this.loggedIn, () => html`
						<td class="nobreak price">${msg('on request')}</td>
					`)}
				</tr>
				<tr>
					<th>${msg('Pole Doors')}</th>
					<td class="grow">
						${selectOlivioSecondDoorLabel(s)}<br>
						${selectOlivioSecondDoorDescription(s)}
					</td>
					<td class="nobreak"></td>
					${when(this.loggedIn, () => html`
						<td class="nobreak price"></td>
					`)}
				</tr>
				<tr>
					<th>${msg('Base')}</th>
					<td class="grow">
						${when( selectOlivioBase(s) === 'buriedbase', () => html`${selectOlivioBuriedBaseSize(s)} mm`)}<br>
						${when(selectOlivioSeparateBuriedBase(s) !== 'none', () => html`${selectOlivioSeparateBuriedBaseDescription(s)}, ${selectOlivioSeparateBuriedBaseHeight(s)}mm<br>`)}
					</td>
					<td class="nobreak">${selectOlivioBaseNumber(s)}</td>
					${when(this.loggedIn, () => html`
						<td class="nobreak price">${msg('on request')}</td>
					`)}
				</tr>
				${when(selectOlivioSeparateBuriedBase(s) !== 'none', () => html`
					<tr>
						<th></th>
						<td class="grow">${selectOlivioSeparateBuriedBaseDescription(s)}, ${selectOlivioSeparateBuriedBaseHeight(s)}mm</td>
						<td class="nobreak">${selectOlivioSeparateBuriedBaseNumber(s)}</td>
						${when(this.loggedIn, () => html`
							<td class="nobreak price">${selectOlivioSeparateBuriedBasePrice(s)} €</td>
						`)}
					</tr>
				`)}
				${when(selectOlivioFuseBox(s) !== 'none', () => html`
					<tr>
						<th>${msg('Fuse Box')}</th>
						<td class="grow">${selectOlivioFuseBoxDescription(s)}</td>
						<td class="nobreak">${selectOlivioFuseBoxNumber(s)}</td>
						${when(this.loggedIn, () => html`
							<td class="nobreak price"></td>
						`)}
					</tr>
				`)}
			</table>
        `;
	}
}
